import type { AppDispatch, RootState } from '../index';
import {
  getProfile,
  upsertProfile,
  type UpsertProfileInput,
} from '../../lib/api/profile-api-client';
import {
  profileFetchFailed,
  profileFetchNotFound,
  profileFetchRequested,
  profileFetchSucceeded,
  profileUpsertFailed,
  profileUpsertRequested,
  profileUpsertSucceeded,
} from '../slices/profile-slice';

/**
 * Profile thunks (round-22 Phase D — realigned to the shipped
 * Smithy contract; `confirmEmailThunk` deleted).
 *
 * Async actions wrapping the two `ProfileApi` v1 operations
 * (`GetProfileV1`, `UpsertProfileV1`) — the only place network
 * side effects live in the profile feature's Redux layer. Each
 * thunk reads the Cognito ID token from `state.auth.idToken`
 * (populated by the auth slice on sign-in success), dispatches
 * the matching `*Requested` action, calls the API client, and
 * dispatches `*Succeeded` / `*Failed` (or
 * `profileFetchNotFound` for the first-time-visitor branch).
 */

function requireIdToken(getState: () => RootState): string {
  const idToken = getState().auth.idToken;
  if (idToken === undefined || idToken === '') {
    throw new Error('not-authenticated');
  }
  return idToken;
}

export function fetchProfile() {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState,
  ): Promise<void> => {
    const idToken = requireIdToken(getState);
    dispatch(profileFetchRequested());
    try {
      const result = await getProfile(idToken);
      if (result.exists && result.profile !== undefined) {
        dispatch(profileFetchSucceeded({ profile: result.profile }));
      } else {
        dispatch(profileFetchNotFound());
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      dispatch(profileFetchFailed({ error: message }));
    }
  };
}

export function upsertProfileThunk(input: UpsertProfileInput) {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState,
  ): Promise<void> => {
    const idToken = requireIdToken(getState);
    dispatch(profileUpsertRequested());
    try {
      const profile = await upsertProfile(input, idToken);
      dispatch(profileUpsertSucceeded({ profile }));
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      dispatch(profileUpsertFailed({ error: message }));
    }
  };
}
